import './styles/styles.scss';

import { useMutation, useQuery } from '@apollo/client';
import EditNoteIcon from '@mui/icons-material/EditNote';
import GroupsIcon from '@mui/icons-material/Groups';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SettingsIcon from '@mui/icons-material/Settings';
import { Button, Grid, MenuItem, Paper, Select, Tooltip, Typography } from '@mui/material';
import { Group, ResolvedEvent, CancelGroupType, Enrollment } from '@thro-types/events';

import clsx from 'clsx';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import CANCL_GROUP_MUTATION from 'gql/mutations/EventService/cancelGroupOfEvent.gql';
import USERS_BY_EMAIL_QUERY from 'gql/queries/EventService/getUsersEmail.gql';

import { DataGrid, GQLGuard, Modal, TypedGridColumns } from 'app/components';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';

import { isPreCalc } from '../../helper';

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  group: Group;
  eventData: ResolvedEvent;
}

interface UserQueryResponse {
  id: number;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export const EventCancelGroupModal = ({ open, setOpen, group, eventData }: Props) => {
  const [selected, setSelected] = useState<string | CancelGroupType>('');
  const [submitting, setSubmitting] = useState(false);
  const manuallyMovedParticipants = useState<{ groupId: number; enrollmentId: number }[]>([]);
  const [cancelGroup] = useMutation(CANCL_GROUP_MUTATION, {
    context: { clientName: 'federation' }
  });
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const clearStates = () => {
    setSelected('');
    manuallyMovedParticipants[1]([]);
  };

  const handleSaveClick = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);

    // The value 0 is the value we use to invalidate a selected participant to group pair
    const participantToGroupRearrangement = manuallyMovedParticipants[0].filter(
      part => part.groupId !== 0
    );

    try {
      await cancelGroup({
        variables: {
          groupId: group.id,
          type: selected,
          participantToGroupRearrangement: participantToGroupRearrangement
        },
        refetchQueries: ['GetEvent']
      });
      dispatch(AlertActions.success(t(translations.pages.events.alerts.successfulGroupCancel())));
      clearStates();
      setOpen(false);
      setSubmitting(false);
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.events.alerts.failedGroupCancel())));
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        clearStates();
        setOpen(false);
      }}
      headline={`${t(translations.pages.events.cancelModal.headline())} ${group?.info}`}
      onCancel={() => {
        clearStates();
        setOpen(false);
      }}
      width="full"
    >
      <Grid container justifyContent={'center'} className="cancelGroupModal">
        <Grid item xs={12} className="selectCard">
          <Grid item>
            <Typography component={'h1'}>
              {t(translations.pages.events.cancelModal.howToCancel())}
            </Typography>
          </Grid>
          <Grid container spacing={2} justifyContent={'space-around'}>
            <Grid item xs={4} onClick={() => setSelected(CancelGroupType.COMPLETE)}>
              <CancelTypeCard
                icon={<HighlightOffIcon />}
                title={t(translations.pages.events.cancelModal.complete())}
                selected={selected === CancelGroupType.COMPLETE}
              />
            </Grid>
            <Grid item xs={4} onClick={() => setSelected(CancelGroupType.AUTOMATIC)}>
              <CancelTypeCard
                icon={<SettingsIcon />}
                title={t(translations.pages.events.cancelModal.automatic())}
                selected={selected === CancelGroupType.AUTOMATIC}
              />
            </Grid>
            <Grid item xs={4} onClick={() => setSelected(CancelGroupType.MANUALLY)}>
              <CancelTypeCard
                icon={<EditNoteIcon />}
                title={t(translations.pages.events.cancelModal.manually())}
                selected={selected === CancelGroupType.MANUALLY}
              />
            </Grid>
          </Grid>
        </Grid>
        {/* IF MANUALLY IS SELECTED */}
        {selected === CancelGroupType.MANUALLY && (
          <Grid container>
            <ManuallyMoveParticipants
              eventData={eventData}
              group={group}
              manuallyMovedParticipants={manuallyMovedParticipants}
            />
          </Grid>
        )}
        {/* BUTTONS */}
        <Grid container justifyContent={'space-between'} style={{ padding: '1em' }}>
          <Button
            variant="outlined"
            onClick={() => {
              clearStates();
              setOpen(false);
            }}
            // disabled={submitting}
          >
            {t(translations.common.cancel())}
          </Button>
          <Button
            key="submit"
            variant="contained"
            color="primary"
            style={{ color: 'white' }}
            type="submit"
            disabled={selected === ''}
            onClick={handleSaveClick}
          >
            {t(translations.common.save())}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

const CancelTypeCard = ({
  icon,
  title,
  selected
}: {
  icon: React.ReactNode;
  title: string;
  selected: boolean;
}) => {
  return (
    <Grid
      component={Paper}
      elevation={3}
      className={clsx('cancelCard', {
        'cancelCard--active': selected
      })}
    >
      <Grid container className="cancelCard__icon">
        {icon}
      </Grid>

      <Grid container justifyContent={'center'} className="cancelCard__title">
        {title}
      </Grid>
    </Grid>
  );
};

const ManuallyMoveParticipants = ({
  eventData,
  group,
  manuallyMovedParticipants
}: {
  eventData: ResolvedEvent;
  group: Group;
  manuallyMovedParticipants: [
    {
      groupId: number;
      enrollmentId: number;
    }[],
    Dispatch<
      SetStateAction<
        {
          groupId: number;
          enrollmentId: number;
        }[]
      >
    >
  ];
}) => {
  const { t } = useTranslation();
  const enrollmentRows = eventData.groups?.find(gp => gp.id === group.id)?.enrollments || [];
  const preCalcEnrollmentRows =
    eventData.groups?.find(gp => gp.id === group.id)?.preCalcEnrollments || [];

  const ids = [
    ...enrollmentRows.map(row => row.userId),
    ...preCalcEnrollmentRows.map(row => row.userId)
  ];

  const availableGroups = eventData.groups?.filter(gp => gp.id !== group.id && !gp.isCancelled);

  const getUsersQuery = useQuery<
    { users: UserQueryResponse[] },
    { ids?: number[]; filter?: string; type?: UserType }
  >(USERS_BY_EMAIL_QUERY, {
    context: { clientName: 'federation' },
    variables: { ids: ids, type: UserType.SHIBBOLETH }
  });

  const getAvailableSeats = useCallback(
    (group: Group) => {
      return (
        group.maxParticipants -
        (group.enrollments?.length ?? 0) -
        (group.preCalcEnrollments?.length ?? 0) -
        manuallyMovedParticipants[0].filter(participant => participant.groupId === group.id).length
      );
    },
    [manuallyMovedParticipants]
  );

  const columns = useMemo((): TypedGridColumns<
    Enrollment & { index: string; firstName: string; lastName: string; email: string }
  > => {
    return [
      {
        field: 'index',
        flex: 1,
        headerName: t(translations.pages.events.eventDetailPage.placement()),
        renderCell: params => (
          <Tooltip title={params.row.index} disableInteractive>
            <span>{params.row.index}</span>
          </Tooltip>
        )
      },
      {
        field: 'userId',
        flex: 1,
        headerName: t(translations.common.userId()),
        renderCell: params => (
          <Tooltip title={params.row.userId} disableInteractive>
            <span>{params.row.userId}</span>
          </Tooltip>
        )
      },
      {
        field: 'createdAt',
        flex: 2,
        headerName: t(translations.pages.events.eventDetailPage.timeOfEnrollment()),
        renderCell: params => {
          const date = new Date(params.row.createdAt).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
          });
          return (
            <Tooltip title={date} disableInteractive>
              <span>{date}</span>
            </Tooltip>
          );
        }
      },
      {
        field: 'firstName',
        flex: 1,
        headerName: t(translations.common.firstName()),
        renderCell: params => (
          <Tooltip title={params.row.firstName} disableInteractive>
            <span>{params.row.firstName}</span>
          </Tooltip>
        )
      },
      {
        field: 'lastName',
        flex: 1,
        headerName: t(translations.common.lastName()),
        renderCell: params => (
          <Tooltip title={params.row.lastName} disableInteractive>
            <span>{params.row.lastName}</span>
          </Tooltip>
        )
      },
      {
        field: 'email',
        flex: 2,
        headerName: t(translations.common.email()),
        renderCell: params => (
          <Tooltip title={params.row.email} disableInteractive>
            <span>{params.row.email}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        headerName: t(translations.pages.events.moveToGroup()),
        field: 'actions',
        // type: 'actions',
        renderCell: params => {
          return (
            <Select
              variant="standard"
              fullWidth={true}
              onChange={value => {
                if (value.target.value !== 0) {
                  return manuallyMovedParticipants[1](old => [
                    ...old.filter(part => part.enrollmentId !== params.row.id),
                    { groupId: value.target.value as number, enrollmentId: params.row.id }
                  ]);
                } else {
                  return manuallyMovedParticipants[1](old => [
                    ...old.filter(part => part.enrollmentId !== params.row.id)
                  ]);
                }
              }}
              defaultValue={0}
            >
              <MenuItem value={0}>{' --- '}</MenuItem>
              {availableGroups?.map(gp => {
                const availableSeats = getAvailableSeats(gp);
                return (
                  <MenuItem disabled={availableSeats <= 0} value={gp.id} key={gp.id}>
                    {gp.info || t(translations.pages.events.groupWithoutInfo())}
                  </MenuItem>
                );
              })}
            </Select>
          );
        }
      }
    ];
  }, [availableGroups, getAvailableSeats, manuallyMovedParticipants, t]);

  return (
    <Grid item xs={12} style={{ padding: '1em' }}>
      <Grid container spacing={2}>
        {availableGroups?.map((gp, index) => {
          const availableSeats = getAvailableSeats(gp);
          return (
            <Grid item key={index}>
              <Grid container spacing={1}>
                <Grid item>
                  <GroupsIcon />
                </Grid>
                <Grid item>{`${
                  gp.info || t(translations.pages.events.groupWithoutInfo())
                }: ${availableSeats}`}</Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
      <GQLGuard query={getUsersQuery}>
        {userData => (
          <Grid item flexGrow={1} style={{ height: '60vh', width: '100%' }} component={Paper}>
            <DataGrid
              style={{ height: '100%', width: '100%' }}
              columns={columns}
              className="table"
              rows={
                isPreCalc(eventData)
                  ? preCalcEnrollmentRows.map((row, index) => ({
                      ...row,
                      index: index + 1,
                      email: userData.users.find(user => user.id === row.userId)?.email ?? '',
                      firstName:
                        userData.users.find(user => user.id === row.userId)?.firstName ?? '',
                      lastName: userData.users.find(user => user.id === row.userId)?.lastName ?? ''
                    }))
                  : enrollmentRows.map((row, index) => ({
                      ...row,
                      index: index + 1,
                      email: userData.users.find(user => user.id === row.userId)?.email ?? '',
                      firstName:
                        userData.users.find(user => user.id === row.userId)?.firstName ?? '',
                      lastName: userData.users.find(user => user.id === row.userId)?.lastName ?? ''
                    }))
              }
              paginationMode={'client'}
            />
          </Grid>
        )}
      </GQLGuard>
    </Grid>
  );
};
